import circle from '@turf/circle';
import { Feature, Polygon, Position } from 'geojson';
import {
    CircleGeometry,
    CommunityLocationDto,
    LocationGeometryType,
    PolygonGeometry
} from '@api/ChallengeClient';

// MapboxDraw polygons repeat the first coordinate as the last; drop it for storage.
export const polygonFeatureToApi = (
    feature: Feature<Polygon>
): PolygonGeometry => {
    const ring = feature.geometry.coordinates[0] ?? [];
    const open = ring.length > 1 ? ring.slice(0, -1) : ring;
    return {
        points: open.map(([longitude, latitude]) => ({ latitude, longitude }))
    };
};

// mapbox-gl-draw-circle stores center ([lng, lat]) + radiusInKm on the feature properties.
export const circleFeatureToApi = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    feature: any
): CircleGeometry => {
    const center: Position = feature.properties.center;
    const radiusInKm: number = feature.properties.radiusInKm;
    return {
        center: { latitude: center[1], longitude: center[0] },
        radiusMeters: radiusInKm * 1000
    };
};

// Build a mapbox-gl-draw-circle feature for a CircleGeometry. Carries the
// isCircle/center/radiusInKm properties so the draw modes can re-edit it.
export const circleGeometryToFeature = (
    geometry: CircleGeometry
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Feature<Polygon, any> => {
    const { center, radiusMeters } = geometry;
    const feature = circle(
        [center.longitude, center.latitude],
        radiusMeters / 1000,
        { units: 'kilometers' }
    ) as Feature<Polygon, any>;
    feature.properties = {
        isCircle: true,
        center: [center.longitude, center.latitude],
        radiusInKm: radiusMeters / 1000
    };
    return feature;
};

// Render an existing location back onto the map when editing, and for the
// static-map thumbnail.
export const apiToGeoJson = (
    location: CommunityLocationDto
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Feature<Polygon, any> => {
    if (
        location.geometryType === LocationGeometryType.CIRCLE &&
        location.circle
    ) {
        return circleGeometryToFeature(location.circle);
    }
    const points = location.polygon?.points ?? [];
    const ring: Position[] = points.map((p) => [p.longitude, p.latitude]);
    if (ring.length) {
        ring.push(ring[0]); // close the ring for GeoJSON
    }
    return {
        type: 'Feature',
        properties: {},
        geometry: { type: 'Polygon', coordinates: [ring] }
    };
};
